import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Card from '../components/Card'
import Button from '../components/Button'
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Lock,
} from 'lucide-react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { getBreachStatistics, getBreachEvents } from '../services/breachDetection'
import { getEncryptionStatus, verifyAllEncryption } from '../services/encryptionVerification'
import { getGDPRComplianceStatus, getGDPRDeletionHistory } from '../services/gdprCompliance'
import { generateHIPAAReport, generateGDPRReport, getComplianceReports, getComplianceScoreTrend } from '../services/complianceReporting'
import { formatDate } from '../utils/helpers'

const ComplianceDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [breachStats, setBreachStats] = useState(null)
  const [breachEvents, setBreachEvents] = useState([])
  const [encryptionStatus, setEncryptionStatus] = useState(null)
  const [gdprStatus, setGdprStatus] = useState(null)
  const [complianceReports, setComplianceReports] = useState([])
  const [hipaaScoreTrend, setHipaaScoreTrend] = useState([])
  const [gdprScoreTrend, setGdprScoreTrend] = useState([])
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const organizationId = useOrganizationId()

  useEffect(() => {
    if (organizationId) {
      fetchAllData()
    }
  }, [organizationId])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      const [
        breaches,
        events,
        encryption,
        gdpr,
        reports,
        hipaaTrend,
        gdprTrend,
      ] = await Promise.all([
        getBreachStatistics(organizationId),
        getBreachEvents(organizationId, { limit: 10 }),
        getEncryptionStatus(organizationId),
        getGDPRComplianceStatus(organizationId),
        getComplianceReports(organizationId, null, 10),
        getComplianceScoreTrend(organizationId, 'hipaa', 6),
        getComplianceScoreTrend(organizationId, 'gdpr', 6),
      ])

      setBreachStats(breaches)
      setBreachEvents(events)
      setEncryptionStatus(encryption)
      setGdprStatus(gdpr)
      setComplianceReports(reports)
      setHipaaScoreTrend(hipaaTrend)
      setGdprScoreTrend(gdprTrend)
    } catch (error) {
      console.error('Error fetching compliance data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyEncryption = async () => {
    setVerifying(true)
    try {
      await verifyAllEncryption(organizationId)
      alert('Encryption verification completed!')
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to verify encryption')
    } finally {
      setVerifying(false)
    }
  }

  const handleGenerateReport = async (type) => {
    try {
      const periodEnd = new Date()
      const periodStart = new Date()
      periodStart.setMonth(periodStart.getMonth() - 1)

      if (type === 'hipaa') {
        await generateHIPAAReport(
          organizationId,
          periodStart.toISOString(),
          periodEnd.toISOString()
        )
      } else {
        await generateGDPRReport(
          organizationId,
          periodStart.toISOString(),
          periodEnd.toISOString()
        )
      }

      alert(`${type.toUpperCase()} report generated successfully!`)
      fetchAllData()
    } catch (error) {
      alert(error.message || 'Failed to generate report')
    }
  }

  const getComplianceColor = (score) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getComplianceBadge = (score) => {
    if (score >= 90) return 'bg-green-100 text-green-700'
    if (score >= 70) return 'bg-yellow-100 text-yellow-700'
    return 'bg-red-100 text-red-700'
  }

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500">Loading compliance dashboard...</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Shield size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Compliance Dashboard</h1>
              <p className="text-gray-600">Monitor HIPAA and GDPR compliance status</p>
            </div>
          </div>
          <Button variant="secondary" onClick={fetchAllData} className="flex items-center gap-2">
            <RefreshCw size={18} />
            Refresh
          </Button>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Shield },
          { id: 'hipaa', label: 'HIPAA', icon: FileText },
          { id: 'gdpr', label: 'GDPR', icon: Lock },
          { id: 'breaches', label: 'Breach Events', icon: AlertTriangle },
        ].map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-700'
                  : 'text-gray-600 hover:text-primary-600'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Compliance Scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card variant="soft" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">HIPAA Compliance</h3>
                <FileText size={20} className="text-blue-600" />
              </div>
              {complianceReports.find((r) => r.report_type === 'hipaa') ? (
                <>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span
                      className={`text-4xl font-bold ${getComplianceColor(
                        complianceReports.find((r) => r.report_type === 'hipaa')
                          ?.compliance_score || 0
                      )}`}
                    >
                      {complianceReports.find((r) => r.report_type === 'hipaa')?.compliance_score?.toFixed(0) || 'N/A'}
                    </span>
                    <span className="text-gray-500">/ 100</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {complianceReports.find((r) => r.report_type === 'hipaa')?.issues_found || 0} issues found
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleGenerateReport('hipaa')}
                  >
                    Generate Report
                  </Button>
                </>
              ) : (
                <div>
                  <p className="text-gray-500 mb-4">No HIPAA report generated yet</p>
                  <Button size="sm" onClick={() => handleGenerateReport('hipaa')}>
                    Generate First Report
                  </Button>
                </div>
              )}
            </Card>

            <Card variant="soft" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">GDPR Compliance</h3>
                <Lock size={20} className="text-green-600" />
              </div>
              {gdprStatus ? (
                <>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span
                      className={`text-4xl font-bold ${getComplianceColor(gdprStatus.complianceScore)}`}
                    >
                      {gdprStatus.complianceScore}
                    </span>
                    <span className="text-gray-500">/ 100</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {gdprStatus.pendingDeletions} pending deletions
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleGenerateReport('gdpr')}
                  >
                    Generate Report
                  </Button>
                </>
              ) : (
                <div>
                  <p className="text-gray-500 mb-4">No GDPR report generated yet</p>
                  <Button size="sm" onClick={() => handleGenerateReport('gdpr')}>
                    Generate First Report
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* Encryption Status */}
          {encryptionStatus && (
            <Card variant="soft" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Encryption Status</h3>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleVerifyEncryption}
                  disabled={verifying}
                >
                  {verifying ? 'Verifying...' : 'Verify Encryption'}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Verified</p>
                  <p className="text-2xl font-bold text-green-600">{encryptionStatus.verified}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{encryptionStatus.failed}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Verified</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {encryptionStatus.lastVerified
                      ? formatDate(encryptionStatus.lastVerified)
                      : 'Never'}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Breach Summary */}
          {breachStats && (
            <Card variant="soft" className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Breach Events Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{breachStats.total}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{breachStats.bySeverity.critical}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Unresolved</p>
                  <p className="text-2xl font-bold text-orange-600">{breachStats.unresolved}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last 30 Days</p>
                  <p className="text-2xl font-bold text-gray-900">{breachStats.last30Days}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* HIPAA Tab */}
      {activeTab === 'hipaa' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">HIPAA Compliance</h2>
            <Button onClick={() => handleGenerateReport('hipaa')}>Generate Report</Button>
          </div>

          {/* Score Trend */}
          {hipaaScoreTrend.length > 0 && (
            <Card variant="soft" className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Score Trend</h3>
              <div className="space-y-2">
                {hipaaScoreTrend.map((report, index) => (
                  <div key={report.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(report.generated_at)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {report.issues_found} issues • {report.issues_resolved} resolved
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-2xl font-bold ${getComplianceColor(report.compliance_score)}`}
                      >
                        {report.compliance_score.toFixed(0)}
                      </span>
                      {index > 0 && (
                        <span>
                          {report.compliance_score > hipaaScoreTrend[index - 1].compliance_score ? (
                            <TrendingUp size={20} className="text-green-600" />
                          ) : (
                            <TrendingDown size={20} className="text-red-600" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Reports */}
          <Card variant="soft" className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reports</h3>
            {complianceReports.filter((r) => r.report_type === 'hipaa').length === 0 ? (
              <p className="text-gray-500">No HIPAA reports generated yet</p>
            ) : (
              <div className="space-y-3">
                {complianceReports
                  .filter((r) => r.report_type === 'hipaa')
                  .map((report) => (
                    <div
                      key={report.id}
                      className="p-4 bg-gray-50 rounded-lg flex items-center justify-between"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">
                          {formatDate(report.period_start)} - {formatDate(report.period_end)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Generated {formatDate(report.generated_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${getComplianceBadge(
                            report.compliance_score
                          )}`}
                        >
                          {report.compliance_score.toFixed(0)}%
                        </span>
                        <Button variant="secondary" size="sm">
                          <Download size={16} className="mr-2" />
                          Export
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* GDPR Tab */}
      {activeTab === 'gdpr' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">GDPR Compliance</h2>
            <Button onClick={() => handleGenerateReport('gdpr')}>Generate Report</Button>
          </div>

          {gdprStatus && (
            <>
              {/* Compliance Status */}
              <Card variant="soft" className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Compliance Score</p>
                    <p className={`text-3xl font-bold ${getComplianceColor(gdprStatus.complianceScore)}`}>
                      {gdprStatus.complianceScore}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending Deletions</p>
                    <p className="text-3xl font-bold text-orange-600">
                      {gdprStatus.pendingDeletions}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Failed Deletions</p>
                    <p className="text-3xl font-bold text-red-600">{gdprStatus.failedDeletions}</p>
                  </div>
                </div>
              </Card>

              {/* Consent Records */}
              <Card variant="soft" className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Consent Records</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {gdprStatus.consentRecords.total}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Granted</p>
                    <p className="text-2xl font-bold text-green-600">
                      {gdprStatus.consentRecords.granted}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Withdrawn</p>
                    <p className="text-2xl font-bold text-red-600">
                      {gdprStatus.consentRecords.withdrawn}
                    </p>
                  </div>
                </div>
              </Card>
            </>
          )}

          {/* Score Trend */}
          {gdprScoreTrend.length > 0 && (
            <Card variant="soft" className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Score Trend</h3>
              <div className="space-y-2">
                {gdprScoreTrend.map((report, index) => (
                  <div key={report.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(report.generated_at)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {report.issues_found} issues • {report.issues_resolved} resolved
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-2xl font-bold ${getComplianceColor(report.compliance_score)}`}
                      >
                        {report.compliance_score.toFixed(0)}
                      </span>
                      {index > 0 && (
                        <span>
                          {report.compliance_score > gdprScoreTrend[index - 1].compliance_score ? (
                            <TrendingUp size={20} className="text-green-600" />
                          ) : (
                            <TrendingDown size={20} className="text-red-600" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Breach Events Tab */}
      {activeTab === 'breaches' && (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Breach Events</h2>

          {breachEvents.length === 0 ? (
            <Card variant="soft" className="p-12 text-center">
              <CheckCircle2 size={48} className="mx-auto mb-4 text-green-400" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Breach Events</h3>
              <p className="text-gray-600">No security breaches detected.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {breachEvents.map((event) => (
                <Card key={event.id} variant="soft" className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{event.description}</h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            event.severity === 'critical'
                              ? 'bg-red-100 text-red-700'
                              : event.severity === 'high'
                              ? 'bg-orange-100 text-orange-700'
                              : event.severity === 'medium'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {event.severity}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            event.status === 'resolved'
                              ? 'bg-green-100 text-green-700'
                              : event.status === 'false_positive'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {event.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Type: {event.event_type.replace(/_/g, ' ')} • Detected:{' '}
                        {formatDate(event.detected_at)}
                      </p>
                    </div>
                  </div>
                  {event.resolution_notes && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700">
                      <strong>Resolution:</strong> {event.resolution_notes}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ComplianceDashboard

